// Health of a device while it is sitting on a customer site.
//
// This is a different question from utils/deviceState.js. That module answers
// "where is this unit" (Available / Deployed / Assigned / RMA / Quarantine),
// which is the right axis for the Stock and Map screens. On a site detail page
// every device is Deployed by definition, so that axis says nothing useful.
// The Locations redesign asks the other question instead: of the units on this
// site, which ones are fine and which need someone to go and look at them.
//
// The four states come from the Locations mockup. Nothing here is a stored
// field — each state is derived from signals the app already records:
//
//   Issue        failed its last bench test, or sitting in Quarantine
//   Low battery  Lightbug reports the pack below LOW_BATTERY_PCT
//   Live         Lightbug has heard from it within STALE_AFTER_DAYS
//   Idle         no contact inside that window, or no telemetry at all
//
// Order matters: a failed unit with a flat battery is an Issue, because that is
// what someone needs to act on first. Idle is the fallback rather than an
// assertion — a device with no Lightbug ID has simply never reported, which is
// not the same as being broken, so it must not read as a fault.

// A pack under this is worth a site visit before it dies completely.
export const LOW_BATTERY_PCT = 20;

// Lightbug units check in regularly; a silent week means it is not reporting.
export const STALE_AFTER_DAYS = 7;

export const SITE_DEVICE_STATES = ["Live", "Idle", "Low battery", "Issue"];

// Tone per state, matching the chip tones used across the redesigned screens.
export const SITE_DEVICE_STATE_TONE = {
  "Live":        "success",
  "Idle":        "neutral",
  "Low battery": "warning",
  "Issue":       "danger",
};

// The two states that mean "someone should look at this", used for the site
// roll-ups (the "n need attention" badge and the index row sub-text).
export const ATTENTION_STATES = ["Issue", "Low battery"];

// Resolve one device to a site health state.
//
// `stats` is the result of App.js's getStats(device) — passed in rather than
// recomputed here so this module stays free of test-definition logic.
// `live` is the lightbugLive entry for the device: { batteryPct, lastSeen }.
export function siteDeviceHealth(device, stats, live, now = Date.now()) {
  if (!device) return "Idle";

  // A bench failure or a quarantine flag outranks anything telemetry says.
  if (device.status === "Quarantine") return "Issue";
  if (stats && stats.overall === "fail") return "Issue";

  const batteryPct = live && live.batteryPct != null ? live.batteryPct : null;
  if (batteryPct != null && batteryPct <= LOW_BATTERY_PCT) return "Low battery";

  // No Lightbug contact at all is Idle, not an Issue — see the note above.
  const lastSeen = live && live.lastSeen ? new Date(live.lastSeen).getTime() : null;
  if (!lastSeen || Number.isNaN(lastSeen)) return "Idle";

  const daysSince = (now - lastSeen) / (1000 * 60 * 60 * 24);
  return daysSince <= STALE_AFTER_DAYS ? "Live" : "Idle";
}

// Count devices by health state. Returns every key so callers can render a full
// set without guarding for missing ones.
export function siteDeviceHealthCounts(rows = []) {
  const counts = SITE_DEVICE_STATES.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
  rows.forEach(r => { counts[r.health] = (counts[r.health] || 0) + 1; });
  return counts;
}

// How many devices on a site need someone to look at them.
export function attentionCount(rows = []) {
  return rows.filter(r => ATTENTION_STATES.includes(r.health)).length;
}
